import React from "react";
import {
  Box,
  useTheme,
  Grid,
  Paper,
  Typography,
  Button,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  MenuItem,
  Select,
  InputLabel,
  Stack,
  Avatar,
  Card,
  CardContent,
  CircularProgress,
  IconButton,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Snackbar,
  Alert,
} from "@mui/material";
import { tokens } from "../theme";
import { useState, useEffect } from "react";
import Header from "../components/Header";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import { DataGrid } from "@mui/x-data-grid";
import {
  MonetizationOn,
  Alarm,
  CheckCircle,
  Warning,
  Settings,
  CalendarMonth,
  CycloneRounded,
  DisabledVisible,
  Check,
  Cancel,
  DateRangeTwoTone,
  AlarmRounded,
} from "@mui/icons-material";
import { LineChart } from "@mui/x-charts/LineChart";
import axios from "axios";
import { useUser } from "../provider/AuthProvider";
import BreadcrumbsBar from "../components/BreadcrumbsBar";
import { useTranslation } from "react-i18next";

const buildIconMap = (t) => ({
  [t("financial_page.stat_active_reminders")]: <Alarm fontSize="large" color="primary" />,
  [t("financial_page.stat_paid_this_month")]: <CheckCircle fontSize="large" color="success" />,
  [t("financial_page.stat_overdue_payments")]: <Warning fontSize="large" color="error" />,
  [t("financial_page.stat_renewals_30d")]: <MonetizationOn fontSize="large" color="info" />,
});
const StatsCard = ({ label, value, iconMap }) => {
  const theme = useTheme();

  return (
    <Card elevation={4} sx={{ p: 2, borderRadius: 3, height: "100%" }}>
      <CardContent>
        <Stack direction="row" spacing={2} alignItems="center">
          <Box>
            {iconMap[label]}
            <Typography variant="subtitle2" color="text.secondary">
              {label}
            </Typography>
            <Typography variant="h5">{value}</Typography>
          </Box>
        </Stack>
      </CardContent>
    </Card>
  );
};

const FinancialPage = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const { t } = useTranslation();
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [openSettingDialog, setOpenSettingDialog] = useState(false);

  const [reminderCycle, setReminderCycle] = useState("monthly");
  const [reminderDay, setReminderDay] = useState(10);
  const [renewalLead, setRenewalLead] = useState(1);
  const [currentReminder, setCurrentReminder] = useState(null);

  // NEW STATES
  const [submitting, setSubmitting] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success", // or "error"
  });

  const { user } = useUser();
  useEffect(() => {
    fetchDashboard();
  }, []);

  useEffect(() => {
    if (currentReminder) {
      setReminderCycle(currentReminder.cycle);
      setReminderDay(currentReminder.reminderDay);
      setRenewalLead(currentReminder.renewalLeadMonths);
    }
  }, [currentReminder]);

  const fetchDashboard = async () => {
    try {
      const res = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/financial/getDashboardData/${user.id}`
      );
      setDashboardData(res.data);
    } catch (err) {
      console.error("Failed to load dashboard:", err);
    } finally {
      setLoading(false);
    }
  };
  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        height="80vh"
      >
        <CircularProgress size={60} />
      </Box>
    );
  }

  const iconMap = buildIconMap(t);
  const stats = [
    { label: t("financial_page.stat_active_reminders"), value: dashboardData.counts.activeReminders },
    { label: t("financial_page.stat_paid_this_month"), value: dashboardData.counts.paidPayments },
    { label: t("financial_page.stat_overdue_payments"), value: dashboardData.counts.overduePayments },
    { label: t("financial_page.stat_renewals_30d"), value: 0 },
  ];

  const tableRows = dashboardData.reminders.map((item) => ({
    paymentId: item.paymentId,
    renewalId: item.renewalId,
    building: item.buildingName,
    unit: `${item.floor}-${item.flat}`,
    tenant: item.tenantName,
    rentStart: item.startDate?.split("T")[0],
    nextDue: item.nextDueDate?.split("T")[0],
    cycle: item.cycle,
    status: item.status,
    renewalLead: `${item.reminderDay} days`,
    start_date: item.leaseStart,
    end_date: item.leaseEnd,
    renewalMonth: item.renewalLeadMonths,
    paymentReminderId: item.paymentReminderId,
    leaseAgreementId: item.leaseAgreementId,
  }));

  const trendLabels = dashboardData.trends.map((item) => item.date);
  const trendCounts = dashboardData.trends.map((item) => item.total);
  const activeCounts = dashboardData.trends.map((item) => item.active);
  const disabledCounts = dashboardData.trends.map((item) => item.disabled);

  const handleSettingsSubmit = async () => {
    setSubmitting(true);
    try {
      await axios.put(
        `${process.env.REACT_APP_BACKEND_URL}/api/financial/update`,
        {
          cycle: reminderCycle,
          reminderDay: Number(reminderDay),
          renewalLeadMonths: Number(renewalLead),
          paymentId: currentReminder?.paymentId,
          renewalId: currentReminder?.renewalId,
          paymentStatus: currentReminder?.status,
        }
      );

      setSnackbar({
        open: true,
        message: t("financial_page.settings_updated"),
        severity: "success",
      });
      await fetchDashboard();
      setOpenSettingDialog(false);
    } catch (error) {
      console.error("Failed to update reminder settings:", error);
      setSnackbar({
        open: true,
        message: t("financial_page.settings_save_failed"),
        severity: "error",
      });
    } finally {
      setSubmitting(false);
    }
  };

  const paymentReminderChartData = {
    xAxis: [{ scaleType: "point", data: trendLabels }],
    series: [
      {
        label: t("chart.total"),
        data: trendCounts,
        color: "#0288d1",
      },
      {
        label: t("chart.active"),
        data: activeCounts,
        color: "#43a047",
      },
      {
        label: t("chart.disabled"),
        data: disabledCounts,
        color: "#e53935",
      },
    ],
  };

  const handleSetting = async (reminder) => {
    const reminderdata = dashboardData.reminders.find(
      (u) => u.paymentId === reminder.paymentId
    );
    setCurrentReminder(reminderdata);
    setOpenSettingDialog(true);
  };
  const columns = [
    {
      field: "building",
      headerName: t("financial_page.col_building"),
      flex: 1.5,
      renderCell: ({ row }) => (
        <Typography fontWeight={500} className="notranslate">
          {row.building}-{row.unit}
        </Typography>
      ),
    },
    {
      field: "PaymentReminder",
      headerName: t("financial_page.col_payment_reminder"),
      flex: 2,
      renderCell: ({ row }) => (
        <List dense>
          <ListItem>
            <ListItemIcon>
              <CalendarMonth />
            </ListItemIcon>
            <ListItemText primary={row.nextDue} />
          </ListItem>
          <ListItem>
            <ListItemIcon>
              <CycloneRounded />
            </ListItemIcon>
            <ListItemText primary={row.cycle} />
          </ListItem>
          <ListItem>
            <ListItemIcon>
              {row.status === "disabled" ? (
                <Cancel
                  color={row.status === "disabled" ? "error" : "success"}
                />
              ) : (
                <Check
                  color={row.status === "disabled" ? "error" : "success"}
                />
              )}
            </ListItemIcon>

            {row.status === "disabled" ? (
              <ListItemText
                primary={row.status}
                primaryTypographyProps={{
                  color: "error",
                }}
              />
            ) : (
              <ListItemText
                primary={row.status}
                primaryTypographyProps={{
                  color: "success",
                }}
              />
            )}
          </ListItem>
        </List>
      ),
    },
    {
      field: "RenewalReminder",
      headerName: t("financial_page.col_renewal_reminder"),
      flex: 3,
      renderCell: ({ row }) => (
        <List dense>
          <ListItem>
            <ListItemIcon>
              <DateRangeTwoTone />
            </ListItemIcon>
            <ListItemText primary={`${row.start_date}~${row.start_date}`} />
          </ListItem>
          <ListItem>
            <ListItemIcon>
              <AlarmRounded />
            </ListItemIcon>
            <ListItemText primary={`${row.renewalMonth}months`} />
          </ListItem>
          <ListItem>
            <ListItemIcon>
              {row.status === "disabled" ? (
                <Cancel
                  color={row.status === "disabled" ? "error" : "success"}
                />
              ) : (
                <Check
                  color={row.status === "disabled" ? "error" : "success"}
                />
              )}
            </ListItemIcon>

            {row.status === "disabled" ? (
              <ListItemText
                primary={row.status}
                primaryTypographyProps={{
                  color: "error",
                }}
              />
            ) : (
              <ListItemText
                primary={row.status}
                primaryTypographyProps={{
                  color: "success",
                }}
              />
            )}
          </ListItem>
        </List>
      ),
    },
    {
      field: "actions",
      headerName: t("financial_page.col_actions"),
      flex: 1,
      renderCell: ({ row }) => (
        <IconButton
          color="primary"
          onClick={(e) => {
            e.stopPropagation(); // ✅ Prevents row selection
            handleSetting(row);
          }}
        >
          <Settings />
        </IconButton>
      ),
    },
  ];
  return (
    <Box className="main-container mt-4">
      <BreadcrumbsBar />
      <Box display="flex" justifyContent="space-around">
        <Grid
          container
          spacing={2}
          columns={{ xs: 2, sm: 8, md: 10 }}
          rows={{ xs: 3, sm: 2, md: 2 }}
          sx={{
            width: "100%",
          }}
        >
          <Grid item size={{ xs: 2, sm: 3, md: 5 }}>
            <Box
              sx={{
                p: 1,
                height: "40vh",
                display: "flex",
                flexDirection: "column",
              }}
              elevation={3}
              justifyContent="space-evenly"
            >
              <Box display="flex" justifyContent="space-evenly" p={2}>
                {stats.map((stat, i) => (
                  <StatsCard key={i} label={stat.label} value={stat.value} iconMap={iconMap} />
                ))}
              </Box>

              <Box mt={2}>
                <Paper
                  sx={{
                    padding: 2,
                    height: "30vh",
                  }}
                  elevation={3}
                >
                  <Typography variant="h6" mb={2}>
                    {t("financial_page.monthly_fee_overview")}
                  </Typography>
                  <ResponsiveContainer width="100%" height={200}>
                    <BarChart data={tableRows}>
                      <XAxis dataKey="name" />
                      <YAxis />
                      <Tooltip />
                      <Bar dataKey="paid" fill="#4caf50" name={t("financial_page.chart_paid")} />
                      <Bar dataKey="overdue" fill="#f44336" name={t("financial_page.chart_overdue")} />
                    </BarChart>
                  </ResponsiveContainer>
                </Paper>
              </Box>
              <Box mt={2}>
                <Paper sx={{ padding: 2, height: "30vh" }} elevation={3}>
                  <Typography variant="h6" mb={2}>
                    {t("financial_page.payment_reminder_trends")}
                  </Typography>
                  <ResponsiveContainer width="100%" height={200}>
                    <LineChart
                      xAxis={paymentReminderChartData.xAxis}
                      series={paymentReminderChartData.series}
                      width={500}
                      height={200}
                    />
                  </ResponsiveContainer>
                </Paper>
              </Box>
            </Box>
          </Grid>
          <Grid item size={{ xs: 2, sm: 5, md: 5 }}>
            <Paper
              sx={{
                padding: 2,
                height: "85vh",
              }}
              elevation={3}
            >
              <DataGrid
                rows={tableRows}
                columns={columns}
                getRowId={(row) => row.paymentId}
                pageSize={5}
                pagination
                initialState={{
                  pagination: {
                    paginationModel: {
                      pageSize: 10,
                    },
                  },
                }}
                pageSizeOptions={[5, 10, 15, 20, 25]}
                getRowHeight={() => "auto"}
                sx={{
                  "& .MuiDataGrid-cell": {
                    display: "flex",
                    justifyContent: "left",
                    alignItems: "center",
                  },
                }}
              />
            </Paper>
          </Grid>
        </Grid>
      </Box>
      <Dialog
        open={openSettingDialog}
        onClose={() => setOpenSettingDialog(false)}
        fullWidth
      >
        <DialogTitle>
          <Box display="flex">
            <Alarm />
            <Typography variant="h6" ml={1} className="notranslate">
              ({currentReminder?.buildingName} - {currentReminder?.floor}-{" "}
              {currentReminder?.flat})
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          <Stack spacing={3} mt={1}>
            <FormControl fullWidth>
              <InputLabel id="cycle-label">{t("financial_page.reminder_cycle")}</InputLabel>
              <Select
                labelId="cycle-label"
                value={reminderCycle}
                onChange={(e) => setReminderCycle(e.target.value)}
                label={t("financial_page.reminder_cycle")}
              >
                <MenuItem value="monthly">{t("financial_page.cycle_monthly")}</MenuItem>
                <MenuItem value="quarterly">{t("financial_page.cycle_3months")}</MenuItem>
                <MenuItem value="half_yearly">{t("financial_page.cycle_6months")}</MenuItem>
                <MenuItem value="yearly">{t("financial_page.cycle_yearly")}</MenuItem>
              </Select>
            </FormControl>

            <TextField
              fullWidth
              label={t("financial_page.reminder_day")}
              type="number"
              value={reminderDay}
              onChange={(e) => setReminderDay(Number(e.target.value))}
              inputProps={{ min: 1, max: 31 }}
            />

            <FormControl fullWidth>
              <InputLabel id="renewal-label">{t("financial_page.renewal_months_label")}</InputLabel>
              <Select
                labelId="renewal-label"
                value={renewalLead}
                onChange={(e) => setRenewalLead(e.target.value)}
                label={t("financial_page.renewal_months")}
              >
                <MenuItem value={1}>{t("financial_page.renewal_1month")}</MenuItem>
                <MenuItem value={2}>{t("financial_page.renewal_2months")}</MenuItem>
                <MenuItem value={3}>{t("financial_page.renewal_3months")}</MenuItem>
              </Select>
            </FormControl>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenSettingDialog(false)}>{t("financial_page.cancel")}</Button>
          <Button
            variant="contained"
            onClick={handleSettingsSubmit}
            disabled={submitting}
          >
            {submitting ? t("financial_page.saving") : t("financial_page.save")}
          </Button>
        </DialogActions>
      </Dialog>
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default FinancialPage;
